import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { vacationsAPI, usersAPI } from '../api/api';
import { Modal, LoadingSpinner } from '../components/Common';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import VacationUserStats from '../components/UI/VacationUserStats';
import HolidaysModal from '../components/UI/HolidaysModal';
import { printTable } from '../utils/printTable';

// Shared vacation / absence calendar: month grid of who is away, plus a
// per-person yearly summary (allowance, taken, planned, remaining).
// Everyone sees everything; users change their own entries, admins anyone's
// and the public holidays (which don't count as working days).
// Next to absences, people log overtime hours (or just a note) per day.

const pad = (n) => String(n).padStart(2, '0');
const toKey = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const todayKey = () => {
  const d = new Date();
  return toKey(d.getFullYear(), d.getMonth(), d.getDate());
};
const keyToDate = (key) => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const workingDays = (start, end, halfDay, holidayMap = {}) => {
  if (!start || !end || end < start) return 0;
  let days = 0;
  for (let d = keyToDate(start); ; d.setDate(d.getDate() + 1)) {
    const key = toKey(d.getFullYear(), d.getMonth(), d.getDate());
    if (key > end) break;
    if (d.getDay() !== 0 && d.getDay() !== 6 && !holidayMap[key]) days += 1;
  }
  return halfDay ? days / 2 : days;
};

// Stable pastel colour per person; golden-angle steps keep neighbouring
// ids (1, 2, 3...) on clearly different hues.
const userColor = (id) => {
  const hue = Math.round((Number(id) * 137.508) % 360);
  return { background: `hsl(${hue} 70% 88%)`, color: `hsl(${hue} 55% 25%)` };
};

const typeStyle = {
  vacation: '',
  sick: 'ring-1 ring-red-400',
  other: 'ring-1 ring-gray-400',
};
const typeIcon = { vacation: '🏖️', sick: '🤒', other: '📌' };

const fmtDays = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(1));
const fmtHours = (n) => {
  const v = Math.round(Number(n || 0) * 100) / 100;
  return `${v > 0 ? '+' : ''}${v} h`;
};

const emptyForm = (userId, date) => ({
  id: null,
  user_id: userId,
  type: 'vacation',
  start_date: date,
  end_date: date,
  half_day: false,
  note: '',
});

const emptyOvertime = (userId, date) => ({
  id: null,
  user_id: userId,
  date,
  hours: '',
  note: '',
});

// Absence vs. overtime / note switch at the top of a new entry's form.
const KindTabs = ({ kind, onAbsence, onOvertime, t }) => (
  <div className='flex rounded-lg border border-gray-300 overflow-hidden'>
    {[
      ['absence', `🏖️ ${t('vacations.kindAbsence')}`, onAbsence],
      ['overtime', `⏱️ ${t('vacations.kindOvertime')}`, onOvertime],
    ].map(([k, label, onClick]) => (
      <button
        type='button'
        key={k}
        onClick={onClick}
        className={`flex-1 px-3 py-1.5 text-sm font-medium ${
          kind === k ? 'bg-[#361826] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
        }`}
      >
        {label}
      </button>
    ))}
  </div>
);

const Vacations = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const isAdmin = user?.userType === 'admin';
  const locale = language === 'de' ? 'de-DE' : 'en-GB';

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [view, setView] = useState(() => {
    try {
      return localStorage.getItem('vacationsView') === 'year' ? 'year' : 'month';
    } catch (err) {
      return 'month';
    }
  });
  const [entries, setEntries] = useState([]);
  const [overtime, setOvertime] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [statsUserId, setStatsUserId] = useState(null);
  const [showHolidays, setShowHolidays] = useState(false);
  const [summary, setSummary] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState(null);
  const [otForm, setOtForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editingAllowance, setEditingAllowance] = useState(null);
  const [allowanceValue, setAllowanceValue] = useState('');

  // Grid: weeks starting Monday, covering the whole month.
  const grid = useMemo(() => {
    const first = new Date(year, month, 1);
    const offset = (first.getDay() + 6) % 7; // Monday = 0
    const start = new Date(year, month, 1 - offset);
    const days = [];
    for (let i = 0; i < 42; i += 1) {
      const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
      days.push({
        key: toKey(d.getFullYear(), d.getMonth(), d.getDate()),
        day: d.getDate(),
        inMonth: d.getMonth() === month,
        weekend: d.getDay() === 0 || d.getDay() === 6,
      });
    }
    // Drop a trailing week that's entirely next month.
    return days.slice(0, days[35].inMonth ? 42 : 35);
  }, [year, month]);

  const load = useCallback(async () => {
    try {
      const [from, to] =
        view === 'year' ? [`${year}-01-01`, `${year}-12-31`] : [grid[0].key, grid[grid.length - 1].key];
      const [entriesRes, summaryRes, holidaysRes, overtimeRes] = await Promise.all([
        vacationsAPI.getAll(from, to),
        vacationsAPI.getSummary(year),
        vacationsAPI.getHolidays(`${year - 1}-01-01`, `${year + 1}-12-31`),
        vacationsAPI.getOvertime(from, to),
      ]);
      setEntries(entriesRes.data.data);
      setOvertime(overtimeRes.data.data);
      setSummary(summaryRes.data.data.users);
      setHolidays(holidaysRes.data.data);
    } catch (err) {
      alert(t('vacations.failedToLoad', { error: err.response?.data?.error || err.message }));
    } finally {
      setLoading(false);
    }
  }, [grid, year, view, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    usersAPI
      .getAll()
      .then((res) => setUsers(res.data.data || []))
      .catch(() => {});
  }, []);

  const changeMonth = (delta) => {
    if (view === 'year') {
      setYear(year + delta);
      return;
    }
    const d = new Date(year, month + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth());
  };

  const changeView = (next) => {
    setView(next);
    try {
      localStorage.setItem('vacationsView', next);
    } catch (err) {
      // just won't be remembered
    }
  };

  // Clicking a month name in the year view opens that month.
  const openMonth = (m) => {
    setMonth(m);
    changeView('month');
  };

  // Day cells of one month for the compact year view (Monday first,
  // leading blanks as null).
  const monthCells = (m) => {
    const offset = (new Date(year, m, 1).getDay() + 6) % 7;
    const count = new Date(year, m + 1, 0).getDate();
    const cells = Array.from({ length: offset }, () => null);
    for (let d = 1; d <= count; d += 1) {
      const dow = new Date(year, m, d).getDay();
      cells.push({ key: toKey(year, m, d), day: d, weekend: dow === 0 || dow === 6 });
    }
    return cells;
  };

  const goToday = () => {
    setYear(now.getFullYear());
    setMonth(now.getMonth());
  };

  const entriesOn = (key) => entries.filter((e) => e.start_date <= key && e.end_date >= key);
  const overtimeOn = (key) => overtime.filter((o) => o.date === key);
  const today = todayKey();
  const holidayMap = useMemo(
    () => Object.fromEntries(holidays.map((h) => [h.date, h.name])),
    [holidays],
  );
  const awayToday = entriesOn(today);

  const openNew = (date = today) => setForm(emptyForm(user?.id, date));
  const openEntry = (entry) =>
    setForm({
      id: entry.id,
      user_id: entry.user_id,
      user_name: entry.user_name,
      type: entry.type,
      start_date: entry.start_date,
      end_date: entry.end_date,
      half_day: !!entry.half_day,
      note: entry.note || '',
    });

  const canEdit = form && (isAdmin || form.user_id === user?.id);

  const openNewOvertime = (date = today) => setOtForm(emptyOvertime(user?.id, date));
  const openOvertime = (o) =>
    setOtForm({
      id: o.id,
      user_id: o.user_id,
      user_name: o.user_name,
      date: o.date,
      hours: o.hours ? String(o.hours) : '',
      note: o.note || '',
    });
  const canEditOt = otForm && (isAdmin || otForm.user_id === user?.id);

  // A new entry can be switched between absence and overtime / note,
  // keeping the person and the (first) date.
  const switchToOvertime = () => {
    setOtForm(emptyOvertime(form.user_id, form.start_date));
    setForm(null);
  };
  const switchToAbsence = () => {
    setForm(emptyForm(otForm.user_id, otForm.date));
    setOtForm(null);
  };

  const saveOvertime = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      user_id: otForm.user_id,
      date: otForm.date,
      hours: Number(otForm.hours) || 0,
      note: otForm.note,
    };
    try {
      if (otForm.id) await vacationsAPI.updateOvertime(otForm.id, payload);
      else await vacationsAPI.createOvertime(payload);
      setOtForm(null);
      load();
    } catch (err) {
      alert(t('vacations.failedToSave', { error: err.response?.data?.error || err.message }));
    } finally {
      setSaving(false);
    }
  };

  const removeOvertime = async () => {
    if (!window.confirm(t('vacations.confirmDelete'))) return;
    try {
      await vacationsAPI.deleteOvertime(otForm.id);
      setOtForm(null);
      load();
    } catch (err) {
      alert(t('vacations.failedToSave', { error: err.response?.data?.error || err.message }));
    }
  };

  const printSummary = () =>
    printTable({
      title: t('vacations.summaryTitle', { year }),
      columns: [
        { header: t('vacations.colName') },
        { header: t('vacations.colAllowance'), align: 'right' },
        { header: t('vacations.colCarryover'), align: 'right' },
        { header: t('vacations.colTaken'), align: 'right' },
        { header: t('vacations.colPlanned'), align: 'right' },
        { header: t('vacations.colRemaining'), align: 'right' },
        { header: t('vacations.colSick'), align: 'right' },
        { header: t('vacations.colOther'), align: 'right' },
        { header: t('vacations.colOvertime'), align: 'right' },
      ],
      rows: summary.map((r) => [
        r.user_name,
        fmtDays(r.allowance),
        fmtDays(r.carryover || 0),
        fmtDays(r.taken),
        fmtDays(r.planned),
        fmtDays(r.remaining),
        fmtDays(r.sick),
        fmtDays(r.other),
        fmtHours(r.overtime_balance),
      ]),
    });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      user_id: form.user_id,
      type: form.type,
      start_date: form.start_date,
      end_date: form.end_date,
      half_day: form.half_day,
      note: form.note,
    };
    try {
      if (form.id) await vacationsAPI.update(form.id, payload);
      else await vacationsAPI.create(payload);
      setForm(null);
      load();
    } catch (err) {
      alert(t('vacations.failedToSave', { error: err.response?.data?.error || err.message }));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(t('vacations.confirmDelete'))) return;
    try {
      await vacationsAPI.delete(form.id);
      setForm(null);
      load();
    } catch (err) {
      alert(t('vacations.failedToSave', { error: err.response?.data?.error || err.message }));
    }
  };

  const saveAllowance = async (userId) => {
    const days = Number(allowanceValue);
    setEditingAllowance(null);
    if (!Number.isFinite(days)) return;
    try {
      await vacationsAPI.setAllowance(userId, days);
      load();
    } catch (err) {
      alert(t('vacations.failedToSave', { error: err.response?.data?.error || err.message }));
    }
  };

  const weekdayNames = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) =>
        new Date(2024, 0, 1 + i).toLocaleDateString(locale, { weekday: 'short' }),
      ),
    [locale],
  );
  const monthTitle =
    view === 'year'
      ? String(year)
      : new Date(year, month, 1).toLocaleDateString(locale, { month: 'long', year: 'numeric' });

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6'>
        <div>
          <h1 className='text-3xl font-bold text-gray-900'>{t('vacations.title')}</h1>
          <p className='text-gray-600 mt-1'>{t('vacations.subtitle')}</p>
        </div>
        <div className='flex flex-col gap-2 sm:flex-row'>
          <button onClick={() => setShowHolidays(true)} className='btn-secondary whitespace-nowrap'>
            🎉 {t('vacations.holidays')}
          </button>
          <button onClick={() => openNewOvertime()} className='btn-secondary whitespace-nowrap'>
            ⏱️ {t('vacations.addOvertime')}
          </button>
          <button onClick={() => openNew()} className='btn-primary whitespace-nowrap'>
            {t('vacations.addAbsence')}
          </button>
        </div>
      </div>

      <div className='card mb-6'>
        <p className='text-sm font-semibold text-gray-700 mb-2'>{t('vacations.awayToday')}</p>
        {awayToday.length === 0 ? (
          <p className='text-sm text-gray-500'>{t('vacations.nobodyAway')}</p>
        ) : (
          <div className='flex flex-wrap gap-2'>
            {awayToday.map((e) => (
              <button
                key={e.id}
                onClick={() => openEntry(e)}
                style={userColor(e.user_id)}
                className={`px-3 py-1 rounded-full text-sm ${typeStyle[e.type]}`}
              >
                {typeIcon[e.type]} {e.user_name} {e.half_day ? '½' : ''} · {t('vacations.to')}{' '}
                {keyToDate(e.end_date).toLocaleDateString(locale)}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className='card mb-6'>
        <div className='flex items-center justify-between mb-4'>
          <div className='flex items-center gap-2'>
            <button
              onClick={() => changeMonth(-1)}
              className='btn-secondary px-3'
              title={view === 'year' ? t('vacations.prevYear') : t('vacations.prevMonth')}
            >
              ‹
            </button>
            <button
              onClick={() => changeMonth(1)}
              className='btn-secondary px-3'
              title={view === 'year' ? t('vacations.nextYear') : t('vacations.nextMonth')}
            >
              ›
            </button>
            <button onClick={goToday} className='btn-secondary'>
              {t('vacations.today')}
            </button>
            <div className='flex rounded-lg border border-gray-300 overflow-hidden ml-2'>
              {['month', 'year'].map((v) => (
                <button
                  key={v}
                  onClick={() => changeView(v)}
                  className={`px-3 py-1.5 text-sm font-medium ${
                    view === v ? 'bg-[#361826] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {v === 'month' ? t('vacations.viewMonth') : t('vacations.viewYear')}
                </button>
              ))}
            </div>
          </div>
          <h2 className='text-xl font-semibold text-gray-800 capitalize'>{monthTitle}</h2>
          <div className='hidden md:flex items-center gap-3 text-xs text-gray-600'>
            {['vacation', 'sick', 'other'].map((type) => (
              <span key={type}>
                {typeIcon[type]} {t(`vacations.type_${type}`)}
              </span>
            ))}
          </div>
        </div>

        {view === 'year' ? (
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4'>
            {Array.from({ length: 12 }, (_, m) => (
              <div key={m} className='border border-gray-200 rounded-lg p-2'>
                <button
                  onClick={() => openMonth(m)}
                  className='w-full text-left text-sm font-semibold text-gray-800 capitalize hover:text-blue-600 mb-1'
                >
                  {new Date(year, m, 1).toLocaleDateString(locale, { month: 'long' })}
                </button>
                <div className='grid grid-cols-7 gap-px text-center'>
                  {weekdayNames.map((name) => (
                    <div key={name} className='text-[10px] text-gray-400 capitalize'>
                      {name.slice(0, 2)}
                    </div>
                  ))}
                  {monthCells(m).map((cell, i) => {
                    if (!cell) return <div key={`b${i}`} />;
                    const dayEntries = entriesOn(cell.key);
                    const dayOvertime = overtimeOn(cell.key);
                    return (
                      <button
                        key={cell.key}
                        onClick={() => openNew(cell.key)}
                        title={[
                          holidayMap[cell.key] && `🎉 ${holidayMap[cell.key]}`,
                          ...dayEntries.map(
                            (e) => `${e.user_name}${e.half_day ? ' ½' : ''} – ${t(`vacations.type_${e.type}`)}`,
                          ),
                          ...dayOvertime.map(
                            (o) => `⏱️ ${o.user_name}${o.hours ? ` ${fmtHours(o.hours)}` : ''}${o.note ? ` – ${o.note}` : ''}`,
                          ),
                        ]
                          .filter(Boolean)
                          .join('\n')}
                        style={
                          !holidayMap[cell.key] && dayEntries.length === 1
                            ? { background: userColor(dayEntries[0].user_id).background }
                            : undefined
                        }
                        className={`h-9 rounded text-[11px] flex flex-col items-center justify-start pt-0.5 hover:ring-1 hover:ring-blue-300 ${
                          holidayMap[cell.key]
                            ? 'bg-red-100 text-red-700 font-semibold'
                            : dayEntries.length > 1
                              ? 'bg-blue-100'
                              : cell.weekend
                                ? 'bg-gray-50 text-gray-400'
                                : 'text-gray-700'
                        } ${cell.key === today ? 'ring-2 ring-[#361826] font-bold' : ''}`}
                      >
                        {cell.day}
                        {dayEntries.length > 0 && (
                          <span className='flex gap-0.5 mt-0.5 flex-wrap justify-center'>
                            {dayEntries.slice(0, 4).map((e) => (
                              <span
                                key={e.id}
                                className={`w-2 h-2 rounded-full ${e.type === 'sick' ? 'ring-1 ring-red-500' : ''}`}
                                style={{ background: userColor(e.user_id).color }}
                              />
                            ))}
                          </span>
                        )}
                        {dayOvertime.length > 0 && <span className='text-[8px] leading-none'>⏱️</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
        <div className='overflow-x-auto'>
          <div className='grid grid-cols-7 min-w-[700px] border-l border-t border-gray-200'>
            {weekdayNames.map((name) => (
              <div
                key={name}
                className='px-2 py-1 text-xs font-medium text-gray-500 bg-gray-50 border-r border-b border-gray-200 capitalize'
              >
                {name}
              </div>
            ))}
            {grid.map((cell) => {
              const dayEntries = entriesOn(cell.key);
              const shown = dayEntries.slice(0, 3);
              return (
                <div
                  key={cell.key}
                  onClick={() => openNew(cell.key)}
                  className={`min-h-[96px] p-1 border-r border-b border-gray-200 cursor-pointer hover:bg-blue-50/50 ${
                    holidayMap[cell.key] ? 'bg-red-50' : cell.weekend ? 'bg-gray-50' : 'bg-white'
                  } ${cell.inMonth ? '' : 'opacity-40'}`}
                >
                  <div
                    className={`text-xs mb-1 w-6 h-6 flex items-center justify-center rounded-full ${
                      cell.key === today ? 'bg-[#361826] text-white font-bold' : 'text-gray-600'
                    }`}
                  >
                    {cell.day}
                  </div>
                  {holidayMap[cell.key] && (
                    <button
                      onClick={(ev) => {
                        ev.stopPropagation();
                        setShowHolidays(true);
                      }}
                      className='block w-full text-left truncate rounded px-1.5 py-0.5 mb-0.5 text-[11px] font-medium bg-red-100 text-red-700'
                      title={holidayMap[cell.key]}
                    >
                      🎉 {holidayMap[cell.key]}
                    </button>
                  )}
                  <div className='space-y-0.5'>
                    {shown.map((e) => (
                      <button
                        key={e.id}
                        onClick={(ev) => {
                          ev.stopPropagation();
                          openEntry(e);
                        }}
                        style={userColor(e.user_id)}
                        className={`block w-full text-left truncate rounded px-1.5 py-0.5 text-[11px] ${typeStyle[e.type]}`}
                        title={`${e.user_name} – ${t(`vacations.type_${e.type}`)}${e.note ? ` – ${e.note}` : ''}`}
                      >
                        {e.type !== 'vacation' ? `${typeIcon[e.type]} ` : ''}
                        {e.user_name}
                        {e.half_day ? ' ½' : ''}
                      </button>
                    ))}
                    {dayEntries.length > shown.length && (
                      <p className='text-[11px] text-gray-500 px-1'>
                        {t('vacations.more', { count: dayEntries.length - shown.length })}
                      </p>
                    )}
                    {overtimeOn(cell.key).map((o) => (
                      <button
                        key={`ot${o.id}`}
                        onClick={(ev) => {
                          ev.stopPropagation();
                          openOvertime(o);
                        }}
                        className='block w-full text-left truncate rounded px-1.5 py-0.5 text-[11px] bg-amber-50 text-amber-800 border border-dashed border-amber-300'
                        title={`${o.user_name}${o.hours ? ` ${fmtHours(o.hours)}` : ''}${o.note ? ` – ${o.note}` : ''}`}
                      >
                        {o.hours ? '⏱️' : '📝'} {o.user_name}
                        {o.hours ? ` ${fmtHours(o.hours)}` : ''}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        )}

        {view === 'year' && (
          <div className='flex flex-wrap gap-3 mt-4 text-xs text-gray-600'>
            <span className='flex items-center gap-1'>
              <span className='w-2.5 h-2.5 rounded-sm bg-red-200' />
              {t('vacations.holiday')}
            </span>
            {summary.map((row) => (
              <span key={row.user_id} className='flex items-center gap-1'>
                <span className='w-2.5 h-2.5 rounded-full' style={{ background: userColor(row.user_id).color }} />
                {row.user_name}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className='card'>
        <div className='flex items-center justify-between mb-3'>
          <h2 className='text-lg font-semibold text-gray-800'>{t('vacations.summaryTitle', { year })}</h2>
          <button onClick={printSummary} className='btn-secondary text-sm'>
            🖨️ {t('vacations.print')}
          </button>
        </div>
        <div className='overflow-x-auto'>
          <table className='min-w-full divide-y divide-gray-200 text-sm'>
            <thead className='bg-gray-50'>
              <tr>
                <th className='px-4 py-2 text-left text-xs font-medium text-gray-500'>{t('vacations.colName')}</th>
                <th className='px-4 py-2 text-center text-xs font-medium text-gray-500'>{t('vacations.colAllowance')}</th>
                <th className='px-4 py-2 text-center text-xs font-medium text-gray-500' title={t('vacations.carryoverHint')}>{t('vacations.colCarryover')}</th>
                <th className='px-4 py-2 text-center text-xs font-medium text-gray-500'>{t('vacations.colTaken')}</th>
                <th className='px-4 py-2 text-center text-xs font-medium text-gray-500'>{t('vacations.colPlanned')}</th>
                <th className='px-4 py-2 text-center text-xs font-medium text-gray-500'>{t('vacations.colRemaining')}</th>
                <th className='px-4 py-2 text-center text-xs font-medium text-gray-500'>{t('vacations.colSick')}</th>
                <th className='px-4 py-2 text-center text-xs font-medium text-gray-500'>{t('vacations.colOther')}</th>
                <th className='px-4 py-2 text-center text-xs font-medium text-gray-500' title={t('vacations.overtimeHint')}>{t('vacations.colOvertime')}</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-gray-100'>
              {summary.map((row) => (
                <tr key={row.user_id} className={row.user_id === user?.id ? 'bg-blue-50/40' : ''}>
                  <td className='px-4 py-2 whitespace-nowrap'>
                    <button
                      onClick={() => setStatsUserId(row.user_id)}
                      className='text-left hover:text-blue-600 hover:underline'
                      title={t('vacations.showStats')}
                    >
                      <span className='inline-block w-3 h-3 rounded-full mr-2 align-middle' style={userColor(row.user_id)} />
                      {row.user_name} <span className='text-gray-400'>📊</span>
                    </button>
                  </td>
                  <td className='px-4 py-2 text-center'>
                    {editingAllowance === row.user_id ? (
                      <input
                        type='number'
                        step='0.5'
                        min='0'
                        value={allowanceValue}
                        onChange={(e) => setAllowanceValue(e.target.value)}
                        onBlur={() => saveAllowance(row.user_id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveAllowance(row.user_id);
                          if (e.key === 'Escape') setEditingAllowance(null);
                        }}
                        className='input-field w-20 text-center py-1'
                        autoFocus
                      />
                    ) : isAdmin ? (
                      <button
                        onClick={() => {
                          setEditingAllowance(row.user_id);
                          setAllowanceValue(String(row.allowance));
                        }}
                        className='text-blue-600 hover:underline'
                        title={t('vacations.allowanceHint')}
                      >
                        {fmtDays(row.allowance)}
                      </button>
                    ) : (
                      fmtDays(row.allowance)
                    )}
                  </td>
                  <td className={`px-4 py-2 text-center ${row.carryover < 0 ? 'text-red-600' : 'text-gray-700'}`}>
                    {row.carryover > 0 ? '+' : ''}
                    {fmtDays(row.carryover || 0)}
                  </td>
                  <td className='px-4 py-2 text-center'>{fmtDays(row.taken)}</td>
                  <td className='px-4 py-2 text-center'>{fmtDays(row.planned)}</td>
                  <td
                    className={`px-4 py-2 text-center font-semibold ${
                      row.remaining < 0 ? 'text-red-600' : 'text-green-700'
                    }`}
                  >
                    {fmtDays(row.remaining)}
                  </td>
                  <td className='px-4 py-2 text-center text-gray-600'>{fmtDays(row.sick)}</td>
                  <td className='px-4 py-2 text-center text-gray-600'>{fmtDays(row.other)}</td>
                  <td
                    className={`px-4 py-2 text-center whitespace-nowrap ${
                      row.overtime_balance < 0 ? 'text-red-600' : 'text-gray-700'
                    }`}
                    title={t('vacations.overtimeThisYear', { hours: fmtHours(row.overtime_hours) })}
                  >
                    {fmtHours(row.overtime_balance)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className='text-xs text-gray-500 mt-3'>{t('vacations.countingNote')}</p>
      </div>

      <Modal
        isOpen={!!form}
        onClose={() => setForm(null)}
        title={form?.id ? t('vacations.editAbsence') : t('vacations.newAbsence')}
        size='sm'
      >
        {form && (
          <form onSubmit={save} className='space-y-4'>
            {!form.id && <KindTabs kind='absence' onAbsence={() => {}} onOvertime={switchToOvertime} t={t} />}
            {!canEdit && (
              <p className='text-sm text-gray-600 bg-gray-50 rounded px-3 py-2'>
                {t('vacations.readOnly', { name: form.user_name })}
              </p>
            )}
            <fieldset disabled={!canEdit} className='space-y-4'>
              {isAdmin ? (
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>{t('vacations.person')}</label>
                  <select
                    value={form.user_id || ''}
                    onChange={(e) => setForm({ ...form, user_id: Number(e.target.value) })}
                    className='input-field'
                  >
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.full_name || u.loginName}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                form.user_name && (
                  <p className='text-sm text-gray-700'>
                    {t('vacations.person')}: <b>{form.user_name}</b>
                  </p>
                )
              )}

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>{t('vacations.type')}</label>
                <div className='flex gap-2'>
                  {['vacation', 'sick', 'other'].map((type) => (
                    <button
                      type='button'
                      key={type}
                      onClick={() => setForm({ ...form, type })}
                      className={`flex-1 px-2 py-2 text-sm rounded-lg border ${
                        form.type === type
                          ? 'bg-[#361826] text-white border-[#361826]'
                          : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {typeIcon[type]} {t(`vacations.type_${type}`)}
                    </button>
                  ))}
                </div>
              </div>

              <div className='grid grid-cols-2 gap-3'>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>{t('vacations.from')}</label>
                  <input
                    type='date'
                    value={form.start_date}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        start_date: e.target.value,
                        end_date: form.end_date < e.target.value ? e.target.value : form.end_date,
                      })
                    }
                    className='input-field'
                    required
                  />
                </div>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>{t('vacations.to')}</label>
                  <input
                    type='date'
                    value={form.end_date}
                    min={form.start_date}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                    className='input-field'
                    required
                  />
                </div>
              </div>

              <label className='flex items-center gap-2 text-sm text-gray-700'>
                <input
                  type='checkbox'
                  checked={form.half_day}
                  onChange={(e) => setForm({ ...form, half_day: e.target.checked })}
                />
                {form.start_date === form.end_date ? t('vacations.halfDay') : t('vacations.halfDays')}
              </label>

              <p className='text-sm text-gray-600'>
                {t('vacations.workingDays', {
                  count: fmtDays(
                    workingDays(form.start_date, form.end_date, form.half_day, holidayMap),
                  ),
                })}
              </p>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>{t('vacations.note')}</label>
                <input
                  type='text'
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  className='input-field'
                  placeholder={t('vacations.notePlaceholder')}
                  maxLength={255}
                />
              </div>
            </fieldset>

            <div className='flex justify-between gap-3 pt-2'>
              <div>
                {form.id && canEdit && (
                  <button type='button' onClick={remove} className='text-sm text-red-600 hover:text-red-800'>
                    {t('vacations.delete')}
                  </button>
                )}
              </div>
              <div className='flex gap-3'>
                <button type='button' onClick={() => setForm(null)} className='btn-secondary'>
                  {t('common.cancel')}
                </button>
                {canEdit && (
                  <button type='submit' className='btn-primary' disabled={saving}>
                    {saving ? t('vacations.saving') : t('vacations.save')}
                  </button>
                )}
              </div>
            </div>
          </form>
        )}
      </Modal>

      <Modal
        isOpen={!!otForm}
        onClose={() => setOtForm(null)}
        title={otForm?.id ? t('vacations.editOvertime') : t('vacations.newOvertime')}
        size='sm'
      >
        {otForm && (
          <form onSubmit={saveOvertime} className='space-y-4'>
            {!otForm.id && <KindTabs kind='overtime' onAbsence={switchToAbsence} onOvertime={() => {}} t={t} />}
            {!canEditOt && (
              <p className='text-sm text-gray-600 bg-gray-50 rounded px-3 py-2'>
                {t('vacations.readOnly', { name: otForm.user_name })}
              </p>
            )}
            <fieldset disabled={!canEditOt} className='space-y-4'>
              {isAdmin ? (
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>{t('vacations.person')}</label>
                  <select
                    value={otForm.user_id || ''}
                    onChange={(e) => setOtForm({ ...otForm, user_id: Number(e.target.value) })}
                    className='input-field'
                  >
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.full_name || u.loginName}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                otForm.user_name && (
                  <p className='text-sm text-gray-700'>
                    {t('vacations.person')}: <b>{otForm.user_name}</b>
                  </p>
                )
              )}

              <div className='grid grid-cols-2 gap-3'>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>{t('vacations.date')}</label>
                  <input
                    type='date'
                    value={otForm.date}
                    onChange={(e) => setOtForm({ ...otForm, date: e.target.value })}
                    className='input-field'
                    required
                  />
                </div>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>{t('vacations.hours')}</label>
                  <input
                    type='number'
                    step='0.25'
                    min='-24'
                    max='24'
                    value={otForm.hours}
                    onChange={(e) => setOtForm({ ...otForm, hours: e.target.value })}
                    className='input-field'
                    placeholder='+2'
                  />
                </div>
              </div>
              <p className='text-xs text-gray-500'>{t('vacations.hoursHint')}</p>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>{t('vacations.note')}</label>
                <textarea
                  value={otForm.note}
                  onChange={(e) => setOtForm({ ...otForm, note: e.target.value })}
                  className='input-field'
                  rows={3}
                  placeholder={t('vacations.overtimeNotePlaceholder')}
                  maxLength={255}
                />
              </div>
            </fieldset>

            <div className='flex justify-between gap-3 pt-2'>
              <div>
                {otForm.id && canEditOt && (
                  <button type='button' onClick={removeOvertime} className='text-sm text-red-600 hover:text-red-800'>
                    {t('vacations.delete')}
                  </button>
                )}
              </div>
              <div className='flex gap-3'>
                <button type='button' onClick={() => setOtForm(null)} className='btn-secondary'>
                  {t('common.cancel')}
                </button>
                {canEditOt && (
                  <button
                    type='submit'
                    className='btn-primary'
                    disabled={saving || (!Number(otForm.hours) && !otForm.note.trim())}
                  >
                    {saving ? t('vacations.saving') : t('vacations.save')}
                  </button>
                )}
              </div>
            </div>
          </form>
        )}
      </Modal>

      <VacationUserStats userId={statsUserId} initialYear={year} onClose={() => setStatsUserId(null)} />

      <HolidaysModal
        isOpen={showHolidays}
        onClose={() => setShowHolidays(false)}
        year={year}
        holidays={holidays}
        isAdmin={isAdmin}
        onChanged={load}
      />
    </div>
  );
};

export default Vacations;
